<template>
  <v-card height="100%" flat color="bg1" class="release-notes-list overflow-y-auto">
    <!-- Page header -->
    <v-list-item two-line :class="$vuetify.theme.isDark ? 'bg2' : 'white'">
      <v-list-item-content>
        <v-list-item-title class="title">Release Notes</v-list-item-title>
        <v-list-item-subtitle>
          Generate, edit and publish release notes to your companies
        </v-list-item-subtitle>
      </v-list-item-content>
      <v-list-item-action class="flex-row align-center" style="gap: 8px">
        <v-btn v-if="$has(permissions.ReleaseNotes.Edit)" color="primary" @click="goToNewRelease">
          <v-icon left>mdi-plus</v-icon>
          New Release
        </v-btn>
        <v-tooltip bottom v-if="$has(permissions.ReleaseNotes.Settings)">
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on" @click="goToSettings">
              <v-icon>mdi-cog-outline</v-icon>
            </v-btn>
          </template>
          <span>Settings</span>
        </v-tooltip>
      </v-list-item-action>
    </v-list-item>
    <v-divider></v-divider>

    <!-- Feature flag off: the server 404s every gated endpoint while ReleaseNotes:Enabled is false -->
    <ReleaseNotesDisabledNotice v-if="featureDisabled" />

    <template v-else>
      <!-- Filters -->
      <div
        class="pa-4 d-flex align-center"
        style="gap: 12px"
        :class="{ 'flex-wrap': $vuetify.breakpoint.smAndDown }">
        <v-chip-group v-model="statusIndex" mandatory active-class="primary--text">
          <v-chip v-for="filter in statusFilters" :key="filter.label" small>
            {{ filter.label }}
          </v-chip>
        </v-chip-group>
        <v-spacer v-if="!$vuetify.breakpoint.smAndDown"></v-spacer>
        <v-sheet color="transparent" :width="$vuetify.breakpoint.smAndDown ? '100%' : 280">
          <v-text-field
            v-model="search"
            outlined
            dense
            hide-details
            clearable
            prepend-inner-icon="mdi-magnify"
            placeholder="Search release notes..."></v-text-field>
        </v-sheet>
      </div>

      <!-- Releases -->
      <v-data-table
        class="elevation-0 px-4 pb-4 rounded-0"
        :footer-props="{
          showFirstLastPage: true,
          firstIcon: 'mdi-chevron-double-left',
          lastIcon: 'mdi-chevron-double-right',
          prevIcon: 'mdi-chevron-left',
          nextIcon: 'mdi-chevron-right',
          itemsPerPageText: 'Row/Page',
          itemsPerPageOptions: [15, 30, 50, 100, 200, 300, 400, 500, -1],
        }"
        :headers="releaseHeaders"
        :items="releases"
        :options.sync="options"
        :server-items-length="total"
        :loading="loading"
        @click:row="openRelease">
        <template v-slot:[`item.name`]="{ item }">
          <h4>{{ item.name }}</h4>
        </template>

        <template v-slot:[`item.status`]="{ item }">
          <ReleaseStatusChip :status="item.status" />
        </template>

        <template v-slot:[`item.jiraVersionName`]="{ item }">
          <span>{{ jiraVersionOf(item) }}</span>
        </template>

        <template v-slot:[`item.lastPublishedAt`]="{ item }">
          <span>{{ formatDateCell(lastPublishedOf(item)) }}</span>
        </template>

        <template v-slot:[`item.createdAt`]="{ item }">
          <span>{{ formatDateCell(createdOf(item)) }}</span>
        </template>

        <template v-slot:no-data>
          <div class="text-center d-flex flex-column">
            <img width="300" src="/img/notFound.svg" class="py-5 my-8" style="opacity: 0.54" />
            <p class="font-weight-medium">No release notes yet!</p>
          </div>
        </template>

        <template v-slot:loading>
          <div class="text-center d-flex flex-column">
            <img width="300" src="/img/loading.svg" class="py-5 my-8" style="opacity: 0.54" />
            <p class="font-weight-medium">Loading release notes...</p>
          </div>
        </template>
      </v-data-table>
    </template>
  </v-card>
</template>

<script>
  import { mapGetters } from 'vuex';
  import permissions from '@Share/config/permissions';
  import { $formatDate, $has } from '@Utils';
  import releaseHeaders from '@ReleaseNotes/config/release.headers';
  import { ReleaseStatus } from '@ReleaseNotes/config/enums';
  import ReleaseStatusChip from '@ReleaseNotes/components/ReleaseStatusChip.vue';
  import ReleaseNotesDisabledNotice from '@ReleaseNotes/components/ReleaseNotesDisabledNotice.vue';

  const DEBOUNCE_MS = 400;

  export default {
    name: 'ReleaseNotesList',
    components: {
      ReleaseStatusChip,
      ReleaseNotesDisabledNotice,
    },
    data() {
      return {
        permissions,
        releaseHeaders,
        timerId: null,
        statusFilters: [
          { label: 'All', value: null },
          { label: 'Draft', value: ReleaseStatus.Draft },
          { label: 'Published', value: ReleaseStatus.Published },
          { label: 'Generating', value: ReleaseStatus.Generating },
          { label: 'Archived', value: ReleaseStatus.Archived },
        ],
      };
    },
    computed: {
      ...mapGetters('RELEASE_NOTES', {
        releases: 'GET_RELEASES',
        total: 'GET_RELEASES_TOTAL',
        loading: 'IS_RELEASES_LOADING',
        featureDisabled: 'IS_RELEASE_NOTES_DISABLED',
      }),
      /**
       * The data table's options proxied straight into Vuex so paging survives navigation,
       * mirroring CompanyList.vue's COMPANY/GET_OPTIONS pattern.
       */
      options: {
        get() {
          return this.$store.getters['RELEASE_NOTES/GET_RELEASE_LIST_OPTIONS'];
        },
        set(value) {
          this.$store.commit('RELEASE_NOTES/SET_RELEASE_LIST_OPTIONS', value);
        },
      },
      search: {
        get() {
          return this.$store.getters['RELEASE_NOTES/GET_RELEASE_LIST_SEARCH'];
        },
        set(value) {
          // `clearable` hands back null; the API expects a string.
          this.$store.commit('RELEASE_NOTES/SET_RELEASE_LIST_SEARCH', value || '');
          this.resetPage();
          this.getDataDebounced();
        },
      },
      /**
       * Index into `statusFilters` for the chip group; the store keeps the raw status value
       * (null = All) that the API actually receives.
       */
      statusIndex: {
        get() {
          const current = this.$store.getters['RELEASE_NOTES/GET_RELEASE_LIST_STATUS'];
          const index = this.statusFilters.findIndex((filter) => filter.value === current);
          return index === -1 ? 0 : index;
        },
        set(value) {
          const filter = this.statusFilters[value] || this.statusFilters[0];
          this.$store.commit('RELEASE_NOTES/SET_RELEASE_LIST_STATUS', filter.value);
          this.resetPage();
          this.getDataDebounced();
        },
      },
    },
    watch: {
      options: {
        deep: true,
        handler() {
          this.getDataDebounced();
        },
      },
    },
    beforeDestroy() {
      if (this.timerId != null && this.timerId !== -1) {
        clearTimeout(this.timerId);
      }
      this.timerId = null;
    },
    methods: {
      $has,
      /**
       * First call runs immediately (on mount, via the options.sync handshake); every later call
       * collapses into a 400ms window. Same idiom as CompanyList.vue:770.
       */
      getDataDebounced() {
        if (this.timerId == null) {
          this.timerId = -1;
          this.$store.dispatch('RELEASE_NOTES/FETCH_RELEASES');
          return;
        }

        // cancel pending call
        clearTimeout(this.timerId);

        // delay new call 400ms
        this.timerId = setTimeout(() => {
          this.$store.dispatch('RELEASE_NOTES/FETCH_RELEASES');
        }, DEBOUNCE_MS);
      },
      /**
       * A changed filter invalidates the current page — go back to the first one.
       * Committing a fresh object keeps the deep options watcher honest.
       */
      resetPage() {
        const current = this.options;
        if (!current || current.page === 1) return;
        this.options = { ...current, page: 1 };
      },
      jiraVersionOf(item) {
        return item.jiraVersionName || item.jiraVersion || '—';
      },
      // Field naming on the list DTO is not pinned down by the API plan, so the likely
      // spellings are probed in order rather than assumed.
      lastPublishedOf(item) {
        return item.lastPublishedAt || item.publishedAt || item.firstPublishedAt || null;
      },
      createdOf(item) {
        return item.createdAt || item.createDate || null;
      },
      formatDateCell(value) {
        return value ? $formatDate(value) : '—';
      },
      openRelease(item) {
        this.$router.push({ name: 'release-notes-detail', params: { id: item.id } });
      },
      goToNewRelease() {
        this.$router.push('/manage-release-notes/new');
      },
      goToSettings() {
        this.$router.push('/manage-release-notes/settings');
      },
    },
  };
</script>

<style scoped lang="scss">
  .release-notes-list {
    ::v-deep tbody tr {
      cursor: pointer;
    }
  }
</style>
